/**
 * Income & expenses — the numerator and the denominator of the DSCR.
 *
 * Rents lead, on their own row with the vacancy basis under them as chips: the
 * basis is what DECIDES which rent qualifies, so it is a high-signal choice in
 * exactly the sense the RTL card's product/purpose chips are, not a dropdown.
 * The five annual expenses follow in an even grid — they are peers, and nothing
 * about one changes what the others mean.
 */
import {
  Section,
  Grid,
  Field2,
  ChipGroup,
  MoneyInput,
  SegmentedGroup,
} from "../../sizersShared/sizerUi";
import { sizerOptionsYesNo } from "../../sizersShared/sizerSelectOptions";
import { VACANCIES } from "../dscrConstants";
import GateError from "./GateError";

export default function IncomeExpensesSection({ form, set, gate }) {
  return (
    <Section
      first
      title="Income & expenses"
      subtitle="Rents are TOTALS across all units. Qualifying rent and monthly expenses are resolved server-side."
    >
      <Grid cols={2}>
        <Field2 label="In-place rents (monthly)" required>
          <MoneyInput
            value={form.totalRents}
            onChange={(v) => set("totalRents", v)}
          />
          <GateError fields={gate} name="totalRents" />
        </Field2>
        <Field2 label="Market rents (monthly)" required>
          <MoneyInput
            value={form.totalMarketRents}
            onChange={(v) => set("totalMarketRents", v)}
          />
          <GateError fields={gate} name="totalMarketRents" />
        </Field2>
      </Grid>

      {/* Leased → MIN(in-place, 105% market); Vacant → 100% market; Vacation
          Rental → MIN(income, 125% market); Partially Leased → leased in-place
          plus vacant at market. All resolved server-side. */}
      <Grid cols={1} sx={{ mt: "24px" }}>
        <Field2
          label="Vacancy basis"
          tip="Decides which rent qualifies: Leased uses the lower of in-place and 105% of market; Vacant uses market."
        >
          <ChipGroup
            sm
            value={form.vacancySelection}
            onChange={(v) => set("vacancySelection", v)}
            options={VACANCIES}
          />
        </Field2>
        {/* Guidelines v8.0: a VACANT refinance takes a −5% leverage reduction
            unless the property was renovated / newly built first. Only asked
            when it can matter (vacant + not a purchase). */}
        {form.vacancySelection === "Vacant" && form.loanPurpose !== "Purchase" && (
          <Field2
            label="Recent renovation / construction?"
            tip="A vacant refinance takes a 5% leverage reduction unless the property was recently renovated or newly constructed."
          >
            <SegmentedGroup
              value={Boolean(form.recentRenovationOrConstruction)}
              onChange={(v) => set("recentRenovationOrConstruction", v)}
              options={sizerOptionsYesNo}
              fullWidth={false}
            />
          </Field2>
        )}
      </Grid>

      <Grid cols={3} sx={{ mt: "26px" }}>
        <Field2 label="Annual taxes">
          <MoneyInput
            value={form.annualTaxes}
            onChange={(v) => set("annualTaxes", v)}
          />
        </Field2>
        <Field2 label="Annual insurance">
          <MoneyInput
            value={form.annualInsurance}
            onChange={(v) => set("annualInsurance", v)}
          />
        </Field2>
        <Field2 label="Annual flood insurance">
          <MoneyInput
            value={form.annualFloodInsurance}
            onChange={(v) => set("annualFloodInsurance", v)}
          />
        </Field2>
        <Field2 label="Annual HOA fees">
          <MoneyInput
            value={form.annualHoaFees}
            onChange={(v) => set("annualHoaFees", v)}
          />
        </Field2>
        <Field2
          label="Other annual expense"
          tip="5-9 unit operating expenses."
        >
          <MoneyInput
            value={form.annualOtherExpense}
            onChange={(v) => set("annualOtherExpense", v)}
          />
        </Field2>
      </Grid>
    </Section>
  );
}
